import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMetadata } from "./MetadataContext";
import { createAutosave } from "../utils/autosave";
import { apiFetch } from "../../core/utils/api";
import { pushDevDebugEvent } from "../../core/utils/devDebugBuffer";
import { moveComponent as moveComponentApi, moveItem as moveItemApi } from "../utils/reorderMove";

const MAX_HISTORY_ENTRIES = 100;
const HISTORY_COALESCE_WINDOW_MS = 900;
const FORMAT_PAINTER_HISTORY_EXCLUDED_FIELDS = new Set([
  "id",
  "type",
  "version",
  "reload_target",
  "created_at",
  "updated_at",
  "order",
  "component",
  "hero",
  "button_collection",
  "website",
  "page",
]);

/**
 * EditContext
 * 
 * Provides editing state and controls for the website editor.
 * Manages selected elements, edit metadata, add mode, and save status.
 */
export const EditContext = createContext();

export const useEdit = () => {
  const context = useContext(EditContext);
  if (!context) {
    throw new Error("useEdit must be used within an EditProvider");
  }
  return context;
};

export const EditProvider = ({ children, websiteId, pageId = null, pageSubdomain = null }) => {
  // Get metadata from MetadataContext
  const metadata = useMetadata();
  
  // React Query client for cache invalidation
  const queryClient = useQueryClient();
  
  // Selected element state
  const [selectedElement, setSelectedElement] = useState(null);
  
  // Add mode state
  const [isAddMode, setIsAddMode] = useState(false);
  const [addModeTarget, setAddModeTarget] = useState(null); // Now stores full target object with order
  
  // Inline editing state - tracks which element + field is being edited inline
  const [inlineEditingElement, setInlineEditingElement] = useState(null); // { id, type, field } or null

  // Edit panel focus request - used to programmatically jump to a specific field in the EditPanel UI
  // (e.g., double-click a Text item in the canvas -> focus/select its "content" field in the panel)
  const [editPanelFocusRequest, setEditPanelFocusRequest] = useState(null); // { elementId, elementType, field, selectAll, requestId }
  const editPanelFocusSeqRef = useRef(0);
  
  // Save status
  const [saveStatus, setSaveStatus] = useState("saved"); // 'saved" | 'saving' | 'error'

  // Format painter state.
  //
  // Mirrors Microsoft Office's Format Painter:
  //   * single-click on the toolbar button arms it for ONE paste, then disarms
  //   * double-click on the toolbar button arms it in "sticky" mode so the
  //     user can paint multiple targets in a row (Esc / second click on the
  //     toolbar button cancels)
  //
  // ``source`` holds a *snapshot* of the source element's identity so the
  // painter survives navigation that may unselect the original element
  // between arming and pasting.
  const [formatPainter, setFormatPainter] = useState({
    active: false,
    sticky: false,
    source: null, // { id, type }
  });

  // Autosave instance
  const autosaveRef = useRef(null);
  // Guard against infinite 412 retry loops (keyed by `${type}:${id}`).
  const versionConflictRetryRef = useRef(new Map());

  // Undo/redo history (stores inverse patches for autosaved updates)
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [historyFlags, setHistoryFlags] = useState({ canUndo: false, canRedo: false });
  const applyingHistoryRef = useRef(false);

  const syncHistoryFlags = useCallback(() => {
    const canUndo = undoStackRef.current.length > 0;
    const canRedo = redoStackRef.current.length > 0;
    setHistoryFlags((prev) =>
      prev.canUndo === canUndo && prev.canRedo === canRedo ? prev : { canUndo, canRedo },
    );
  }, []);
  
  // Initialize autosave synchronously so early user interactions (or tests)
  // can't hit "Autosave not initialized" before effects run.
  if (!autosaveRef.current) {
    autosaveRef.current = createAutosave(700);
  }

  const cloneForHistory = useCallback((value) => {
    if (value == null) return value;
    if (typeof value !== "object") return value;
    try {
      // structuredClone is widely supported in modern browsers; fall back to JSON clone.
      // eslint-disable-next-line no-undef
      if (typeof structuredClone === "function") return structuredClone(value);
    } catch (_) {}
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_) {
      // Best-effort: keep by reference if cloning fails (e.g. File)
      return value;
    }
  }, []);

  const shouldIgnoreUndoShortcutTarget = useCallback((target) => {
    if (!(target instanceof Element)) return false;
    // Let native inputs keep their own undo stack.
    if (target.closest("input,textarea,select,[contenteditable='true']")) return true;
    // Allow explicit opt-out for custom widgets (e.g. rich editors).
    if (target.closest("[data-disable-editor-undo='true']")) return true;
    return false;
  }, []);

  // Derive editMetadata based on the selected element's type
  const editMetadata = useMemo(() => {
    if (!selectedElement || !selectedElement.type) {
      return null;
    }

    // Map element types to metadata keys
    const typeToMetadataKey = {
      'hero': 'hero',
      'title': 'title',
      'text': 'text',
      'image': 'image',
      'video': 'video',
      'button': 'button',
      'button_collection': 'buttonCollection',
      'file': 'file',
      'image_collection': 'imageCollection',
      'component': 'component',
      'navbar': 'navbar',
      'navbar_section': 'navbarSection',
      'sidebar': 'sidebar',
      'sidebar_section': 'sidebarSection',
      'footer': 'footer',
      'website_page': 'page',
    };

    const metadataKey = typeToMetadataKey[selectedElement.type];
    if (!metadataKey) {
      console.warn('[EditContext] Unknown element type:', selectedElement.type);
      return null;
    }

    return metadata[metadataKey];
  }, [selectedElement, metadata]);

  // Dev-only: expose a lightweight editor snapshot for debugging render/update loops.
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (typeof window === "undefined") return;
    try {
      const snapshot = {
        ts: Date.now(),
        websiteId,
        pageId,
        pageSubdomain,
        saveStatus,
        isAddMode,
        addModeTarget: addModeTarget
          ? {
              parentType: addModeTarget.parentType,
              parentId: addModeTarget.parentId,
              order: addModeTarget.order,
            }
          : null,
        selectedElement: selectedElement
          ? { id: selectedElement.id, type: selectedElement.type }
          : null,
      };
      window.__VITAE_EDITOR_STATE__ = snapshot;
      pushDevDebugEvent({ type: "editor-snapshot", snapshot });
    } catch (_) {}
  }, [websiteId, pageId, pageSubdomain, saveStatus, isAddMode, addModeTarget, selectedElement]);

  // Get update endpoint for an element
  const getUpdateEndpoint = useCallback((element) => {
    const { type, id, subdomain } = element;
    
    // Item types
    const itemTypes = ['hero', 'title', 'text', 'image', 'video', 'button', 'file'];
    if (itemTypes.includes(type)) {
      return `/api/items/${type}/${id}/update/`;
    }
    
    // Component
    if (type === 'component') {
      return `/api/components/${id}/update/`;
    }
    
    // Button collection
    if (type === 'button_collection') {
      return `/api/items/button_collection/${id}/update/`;
    }
    
    // Image collection
    if (type === 'image_collection') {
      return `/api/imagecollections/${id}/update/`;
    }
    
    // Navbar
    if (type === 'navbar') {
      // Navbar endpoint requires website_id, not navbar id
      return `/api/websites/${element.website}/navbar/update/`;
    }
    
    // Navbar section
    if (type === 'navbar_section') {
      return `/api/websites/navbar/sections/${id}/update/`;
    }
    
    // Sidebar section
    if (type === 'sidebar_section') {
      const side = element.side || element.sidebar?.side;
      const sidebarType = side === 'left' ? 'leftsidebar' : 'rightsidebar';
      return `/api/websites/${sidebarType}/sections/${id}/update/`;
    }
    
    // Sidebar (left or right)
    if (type === 'sidebar') {
      // Sidebar endpoint requires website_id, not sidebar id
      const side = element.side;
      const sidebarType = side === 'left' ? 'leftsidebar' : 'rightsidebar';
      return `/api/websites/${element.website}/${sidebarType}/update/`;
    }
    
    // Footer
    if (type === 'footer') {
      return `/api/websites/footer/${id}/update/`;
    }
    
    // Website page
    if (type === 'website_page') {
      // Prefer id-based updates so autosave remains stable even if the user edits the subdomain.
      const hasId =
        (typeof id === "number" && Number.isFinite(id)) ||
        (typeof id === "string" && id.trim().length > 0);

      if (hasId) {
        return `/api/websites/${websiteId}/pages/${id}/update/`;
      }

      if (subdomain) {
        return `/api/websites/${websiteId}/pages/${subdomain}/update/`;
      }

      console.error('[EditContext] Missing id/subdomain for website_page update:', element);
      return null;
    }
    
    console.error('[EditContext] Unknown element type for update:', type);
    return null;
  }, [websiteId]);

  const getFetchEndpointForVersionRecovery = useCallback((elementType, elementId) => {
    const itemTypes = ['hero', 'title', 'text', 'image', 'video', 'button', 'button_collection', 'file', 'image_collection'];
    if (itemTypes.includes(elementType)) {
      return `/api/items/${elementType}/${elementId}/`;
    }
    if (elementType === "component") {
      return `/api/components/${elementId}/`;
    }
    return null;
  }, []);

  const normaliseBackgroundPayloadForElement = useCallback((element, fieldName, incomingValue) => {
    if (!element) return incomingValue;

    const currentBackground = element.background || {};
    const currentPattern = currentBackground.background_pattern || {};

    if (fieldName === "background_pattern") {
      if (typeof incomingValue === "string") {
        return { ...currentPattern, pattern: incomingValue };
      }
      if (Array.isArray(incomingValue)) {
        return { ...currentPattern, colors: incomingValue };
      }
      return incomingValue;
    }

    if (fieldName === "background" && incomingValue && typeof incomingValue === "object") {
      const nextBackground = { ...incomingValue };
      const nextPattern = nextBackground.background_pattern;

      if (typeof nextPattern === "string") {
        nextBackground.background_pattern = { ...currentPattern, pattern: nextPattern };
      } else if (Array.isArray(nextPattern)) {
        nextBackground.background_pattern = { ...currentPattern, colors: nextPattern };
      } else if (nextPattern && typeof nextPattern === "object") {
        nextBackground.background_pattern = { ...currentPattern, ...nextPattern };
      }

      return nextBackground;
    }

    if (fieldName === "pattern" || fieldName === "colors") {
      return fieldName === "pattern"
        ? { ...currentPattern, pattern: incomingValue }
        : { ...currentPattern, colors: incomingValue };
    }

    return incomingValue;
  }, []);

  const toApiPayloadValue = useCallback((fieldName, value) => {
    if (fieldName === "link_internal") {
      if (value && typeof value === "object") {
        const maybeId = value.id;
        if (typeof maybeId === "number" && Number.isFinite(maybeId)) return maybeId;
        if (typeof maybeId === "string" && maybeId.trim()) {
          const parsed = parseInt(maybeId, 10);
          if (!Number.isNaN(parsed)) return parsed;
        }
      }
    }
    return value;
  }, []);

  const recordHistoryEntry = useCallback((element, updates, { coalesce = true } = {}) => {
    if (!element || !element.type || element.id == null) return;
    if (applyingHistoryRef.current) return;

    const now = Date.now();
    const last = undoStackRef.current.length > 0 ? undoStackRef.current[undoStackRef.current.length - 1] : null;

    const entry = {
      ts: now,
      element: {
        id: element.id,
        type: element.type,
        version: element.version,
        website: element.website,
        side: element.side,
        sidebar: element.sidebar,
        subdomain: element.subdomain,
        __editorParentType: element.__editorParentType,
        __editorParentNavbarId: element.__editorParentNavbarId,
        __editorParentWebsiteId: element.__editorParentWebsiteId,
        component: element.component,
        hero: element.hero,
      },
      updates, // { fieldName: { from, to } }
    };

    // Coalesce rapid successive changes to the same field (e.g. sliders)
    const canCoalesce =
      coalesce &&
      last &&
      last.element?.id === entry.element.id &&
      last.element?.type === entry.element.type &&
      (now - last.ts) < HISTORY_COALESCE_WINDOW_MS &&
      Object.keys(last.updates || {}).length === 1 &&
      Object.keys(entry.updates || {}).length === 1;

    if (canCoalesce) {
      const field = Object.keys(entry.updates)[0];
      const lastField = Object.keys(last.updates)[0];
      if (field === lastField) {
        last.ts = now;
        last.updates[field] = { ...last.updates[field], to: entry.updates[field].to };
      } else {
        undoStackRef.current.push(entry);
      }
    } else {
      undoStackRef.current.push(entry);
    }

    // New user edit invalidates redo history
    if (redoStackRef.current.length) {
      redoStackRef.current = [];
    }

    // Enforce max size
    if (undoStackRef.current.length > MAX_HISTORY_ENTRIES) {
      undoStackRef.current.splice(0, undoStackRef.current.length - MAX_HISTORY_ENTRIES);
    }

    syncHistoryFlags();
  }, [syncHistoryFlags]);

  // Refresh element after AI changes / autosave completes.
  // Invalidate every Hero child item query after a hero update.
  //
  // Many hero updates have *side effects* on the hero's slotted children:
  //   * Switching ``hero_mode`` (or toggling ``manual_layout`` off) re-pins
  //     each slotted Title/Text's ``character_style.text_position`` and zeros
  //     their margin/padding via ``apply_to_hero`` on the backend.
  //   * Promoting a hidden item into a slot via ``/api/items/hero/<id>/assign-slot/``
  //     does the same re-pin against the freshly-promoted child.
  //
  // Children render through ``<Item>``, which uses its own per-item React Query
  // (``["item", type, id]``) with a 30s ``staleTime``. Without explicitly
  // invalidating those queries the editor keeps showing the *pre-mutation*
  // ``character_style`` even though the DB has the new values — the mode
  // picker visibly flips but the title/text don't visually relocate until
  // the user manually refreshes or clicks the same element. The fix is
  // cheap (per-item queries refetch only when actually mounted) and keeps
  // the cache honest.
  //
  // The hero payload from the server already carries the children list as
  // minimal ``{id, type, order}`` refs (see ``HeroSerializer.get_children``),
  // which is exactly what we need to drive the invalidation.
  const invalidateHeroChildrenQueries = useCallback((heroPayload) => {
    if (!heroPayload || !Array.isArray(heroPayload.children)) return;
    heroPayload.children.forEach((child) => {
      if (!child || !child.type || child.id == null) return;
      queryClient.invalidateQueries({
        queryKey: ["item", child.type, child.id],
      });
    });
  }, [queryClient]);

  // NOTE: This must be defined BEFORE callbacks that reference it in dependency arrays,
  // otherwise React will evaluate those arrays before `refreshElement` is initialized,
  // causing: "Cannot access 'refreshElement' before initialization".
  const refreshElement = useCallback(async (elementId, elementType) => {
    if (!elementId || !elementType) {
      console.warn('[EditContext] refreshElement called without elementId or elementType');
      return;
    }
    
    try {
      const itemTypes = ['hero', 'title', 'text', 'image', 'video', 'button', 'file', 'button_collection', 'image_collection'];
      const navTypes = [
        'navbar',
        'navbar_section',
        'sidebar',
        'sidebar_section',
        'leftsidebar',
        'rightsidebar',
        'footer',
      ];
      
      if (itemTypes.includes(elementType)) {
        // Invalidate item query to trigger refetch
        queryClient.invalidateQueries({ queryKey: ["item", elementType, elementId] });
        console.log(`[EditContext] Invalidated query for refresh: ["item", "${elementType}", ${elementId}]`);
        
        // Also fetch fresh data for selected element
        const endpoint = `/api/items/${elementType}/${elementId}/`;
        const response = await apiFetch(endpoint, { credentials: 'include' });
        
        if (response.ok) {
          const updatedData = await response.json();

          // Hero updates can mutate slotted children's character_style /
          // spacing via ``apply_to_hero`` (mode switch, slot promote).
          // Invalidate each child's query so it refetches with fresh
          // character_style — without this the editor keeps the stale
          // ``text_position`` until the user touches the title/text.
          if (elementType === "hero") {
            invalidateHeroChildrenQueries(updatedData);
          }

          // If this item is inside a hero, also invalidate the parent hero query
          if (updatedData.hero) {
            queryClient.invalidateQueries({ queryKey: ["item", "hero", updatedData.hero] });
            console.log(`[EditContext] Invalidated parent Hero query for refresh: ["item", "hero", ${updatedData.hero}]`);
          }

          // If this item is inside a component, invalidate the parent component query.
          // Many pages render items from the component payload (not from per-item queries),
          // so without this the UI can appear "stuck" even though the item updated in the DB.
          if (updatedData.component) {
            queryClient.invalidateQueries({ queryKey: ["component", updatedData.component] });
            console.log(`[EditContext] Invalidated parent Component query for refresh: ["component", ${updatedData.component}]`);
          }

          // If this item is inside a button collection, invalidate the parent collection query.
          if (updatedData.button_collection) {
            queryClient.invalidateQueries({ queryKey: ["item", "button_collection", updatedData.button_collection] });
            console.log(
              `[EditContext] Invalidated parent ButtonCollection query for refresh: ["item", "button_collection", ${updatedData.button_collection}]`,
            );
          }
          
          // If this is the selected element, update it
          if (selectedElement && selectedElement.id === elementId && selectedElement.type === elementType) {
            setSelectedElement(prev => ({ ...prev, ...updatedData }));
          }
        }
      } else if (elementType === 'component') {
        // Invalidate component query to trigger refetch
        queryClient.invalidateQueries({ queryKey: ["component", elementId] });
        console.log(`[EditContext] Invalidated query for refresh: ["component", ${elementId}]`);
        
        // Fetch fresh data for selected element
        const endpoint = `/api/components/${elementId}/`;
        const response = await apiFetch(endpoint, { credentials: 'include' });
        
        if (response.ok) {
          const updatedData = await response.json();
          if (selectedElement && selectedElement.id === elementId && selectedElement.type === elementType) {
            setSelectedElement(prev => ({ ...prev, ...updatedData }));
          }
        }
      } else if (elementType === 'website_page') {
        // Page changes should refresh the page skeleton (which drives component lists/layout)
        if (websiteId) {
          queryClient.invalidateQueries({ queryKey: ["page-skeleton", websiteId] });
          queryClient.invalidateQueries({ queryKey: ["website", websiteId] });
          console.log(`[EditContext] Invalidated page skeleton for refresh: ["page-skeleton", ${websiteId}]`);
        }
      } else if (navTypes.includes(elementType)) {
        // Navigation changes are fetched outside React Query in EditWebsitePage; dispatch an event so it can refetch.
        if (websiteId) {
          queryClient.invalidateQueries({ queryKey: ["page-skeleton", websiteId] });
          queryClient.invalidateQueries({ queryKey: ["website", websiteId] });
        }
        window.dispatchEvent(
          new CustomEvent("editor:refresh-website", {
            detail: { websiteId, reason: "navigation", elementType, elementId },
          }),
        );
      } else {
        console.warn('[EditContext] Unknown element type for refresh:', elementType);
        // Best-effort fallback: invalidate queries and ask EditWebsitePage to refetch website state.
        queryClient.invalidateQueries();
        window.dispatchEvent(
          new CustomEvent("editor:refresh-website", {
            detail: { websiteId, reason: "unknown-refresh-target", elementType, elementId },
          }),
        );
      }
      
    } catch (error) {
      console.error('[EditContext] Failed to refresh element:', error);
      // Best-effort fallback without a full reload
      queryClient.invalidateQueries();
      window.dispatchEvent(
        new CustomEvent("editor:refresh-website", {
          detail: { websiteId, reason: "refresh-error", error: String(error) },
        }),
      );
    }
  }, [selectedElement, queryClient, websiteId, invalidateHeroChildrenQueries]);

  const dispatchNavbarRefreshForElement = useCallback(
    (element, reason = "autosave") => {
      if (element?.__editorParentType !== "navbar") return;
      const navbarWebsiteId = element.__editorParentWebsiteId ?? websiteId ?? null;
      if (!navbarWebsiteId) return;

      try {
        window.dispatchEvent(
          new CustomEvent("editor:refresh-navbar", {
            detail: {
              websiteId: navbarWebsiteId,
              reason,
              elementType: element.type,
              elementId: element.id,
              parentNavbarId: element.__editorParentNavbarId ?? null,
            },
          }),
        );
      } catch (_) {}
    },
    [websiteId],
  );

  const areHistoryValuesEqual = useCallback((left, right) => {
    if (Object.is(left, right)) return true;
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch (_) {
      return false;
    }
  }, []);

  const buildFormatPainterHistoryUpdates = useCallback(
    (beforeElement, afterElement) => {
      if (!beforeElement || !afterElement) return {};
      const updates = {};

      for (const [field, nextValue] of Object.entries(afterElement)) {
        if (FORMAT_PAINTER_HISTORY_EXCLUDED_FIELDS.has(field)) continue;
        if (!Object.prototype.hasOwnProperty.call(beforeElement, field)) continue;

        const prevValue = cloneForHistory(beforeElement[field]);
        const clonedNextValue = cloneForHistory(nextValue);
        if (areHistoryValuesEqual(prevValue, clonedNextValue)) continue;

        updates[field] = {
          from: prevValue,
          to: clonedNextValue,
        };
      }

      return updates;
    },
    [areHistoryValuesEqual, cloneForHistory],
  );

  const pushHistoryEntry = useCallback((entry) => {
    if (!entry || applyingHistoryRef.current) return;

    undoStackRef.current.push(entry);

    if (redoStackRef.current.length) {
      redoStackRef.current = [];
    }

    if (undoStackRef.current.length > MAX_HISTORY_ENTRIES) {
      undoStackRef.current.splice(0, undoStackRef.current.length - MAX_HISTORY_ENTRIES);
    }

    syncHistoryFlags();
  }, [syncHistoryFlags]);

  // ---------------------------------------------------------------------------
  // Format painter
  // ---------------------------------------------------------------------------

  // Canvas element types that can act as source/target for the format painter.
  // ``image_collection`` is intentionally excluded (no shared style fields).
  // Navbar / sidebar sections are included so users can paint the look of one
  // navigation link onto another, or paint a Text/Title's typography onto a
  // section (the backend bridges through ``section.label`` to make it land).
  // ``footer`` is included so users can copy background / base_style /
  // container_effect between the footer and any component or hero.
  const FORMAT_PAINTER_ITEM_TYPES = useMemo(
    () => new Set([
      "component",
      "hero",
      "title",
      "text",
      "image",
      "video",
      "button",
      "file",
      "button_collection",
      "navbar_section",
      "sidebar_section",
      "footer",
    ]),
    [],
  );

  const isFormatPainterEligible = useCallback(
    (element) => {
      if (!element || !element.type || element.id == null) return false;
      return FORMAT_PAINTER_ITEM_TYPES.has(element.type);
    },
    [FORMAT_PAINTER_ITEM_TYPES],
  );

  const armFormatPainter = useCallback(
    (sourceElement, { sticky = false } = {}) => {
      if (!isFormatPainterEligible(sourceElement)) {
        console.warn(
          "[EditContext] Cannot arm format painter for element:",
          sourceElement,
        );
        return false;
      }
      setFormatPainter({
        active: true,
        sticky: !!sticky,
        source: { id: sourceElement.id, type: sourceElement.type },
      });
      return true;
    },
    [isFormatPainterEligible],
  );

  const clearFormatPainter = useCallback(() => {
    setFormatPainter({ active: false, sticky: false, source: null });
  }, []);

  // Apply the currently armed format to ``targetElement``.
  // Returns true if a paint was attempted (even if it failed), false if the
  // painter wasn't armed or the target is ineligible/identical to source.
  const applyFormatPaint = useCallback(
    async (targetElement) => {
      const painter = formatPainter;
      if (!painter.active || !painter.source) return false;
      if (!isFormatPainterEligible(targetElement)) return false;
      if (
        painter.source.id === targetElement.id &&
        painter.source.type === targetElement.type
      ) {
        // Painting onto self is a no-op; in non-sticky mode disarm anyway.
        if (!painter.sticky) clearFormatPainter();
        return false;
      }

      setSaveStatus("saving");

      try {
        const response = await apiFetch("/api/items/format-painter/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            source_type: painter.source.type,
            source_id: painter.source.id,
            target_type: targetElement.type,
            target_id: targetElement.id,
            categories: ["style", "spacing"],
          }),
        });

        if (!response.ok) {
          let message = `Format painter failed (${response.status})`;
          try {
            const data = await response.json();
            message = data?.error || data?.detail || message;
          } catch (_) {}
          console.error("[EditContext] Format painter error:", message);
          setSaveStatus("error");
        } else {
          let data = {};
          try {
            data = await response.json();
          } catch (_) {
            data = {};
          }

          const historyUpdates = buildFormatPainterHistoryUpdates(targetElement, data);
          if (Object.keys(historyUpdates).length > 0) {
            recordHistoryEntry(
              { ...targetElement, version: data?.version ?? targetElement.version },
              historyUpdates,
              { coalesce: false },
            );
          }

          // If the painted target is currently selected, merge the freshly-formatted
          // payload so the EditPanel reflects the new style values immediately.
          if (
            selectedElement &&
            selectedElement.id === targetElement.id &&
            selectedElement.type === targetElement.type
          ) {
            setSelectedElement((prev) => (prev ? { ...prev, ...data } : prev));
          }

          // Refresh the painted target itself, plus the outer container if the
          // backend told us about one (hero/component/button_collection).
          try {
            await refreshElement(targetElement.id, targetElement.type);
          } catch (_) {}

          const reloadTarget = data?.reload_target;
          if (
            reloadTarget &&
            reloadTarget.element_type &&
            reloadTarget.element_id != null
          ) {
            try {
              await refreshElement(reloadTarget.element_id, reloadTarget.element_type);
            } catch (_) {}
          }

          dispatchNavbarRefreshForElement(targetElement, "format-painter");

          setSaveStatus("saved");
        }
      } catch (err) {
        console.error("[EditContext] Format painter request failed:", err);
        setSaveStatus("error");
      }

      // One-shot vs sticky behaviour. Always disarm one-shot, even on error,
      // so the editor can re-arm cleanly without a stale selection in the
      // painter button.
      if (!painter.sticky) {
        clearFormatPainter();
      }

      return true;
    },
    [
      formatPainter,
      isFormatPainterEligible,
      selectedElement,
      buildFormatPainterHistoryUpdates,
      recordHistoryEntry,
      refreshElement,
      dispatchNavbarRefreshForElement,
      clearFormatPainter,
    ],
  );

  // Esc cancels the painter regardless of mode.
  useEffect(() => {
    if (!formatPainter.active) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        clearFormatPainter();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [formatPainter.active, clearFormatPainter]);

  const schedulePatchForElement = useCallback((element, rawUpdates, { optimistic = true } = {}) => {
    if (!element) return;
    const endpoint = getUpdateEndpoint(element);
    if (!endpoint) return;
    if (!autosaveRef.current) return;

    const updates = {};
    const payload = {};
    for (const [fieldName, rawValue] of Object.entries(rawUpdates || {})) {
      const normalized = normaliseBackgroundPayloadForElement(element, fieldName, rawValue);
      updates[fieldName] = normalized;
      payload[fieldName] = toApiPayloadValue(fieldName, normalized);
    }

    // Optimistically update selectedElement if it matches (keeps UX snappy)
    if (optimistic && selectedElement && selectedElement.id === element.id && selectedElement.type === element.type) {
      setSelectedElement((prev) => {
        if (!prev) return prev;
        if (prev.id !== element.id || prev.type !== element.type) return prev;
        return { ...prev, ...updates };
      });
    }

    // Include version if present; autosave will prefer lastKnownVersion per endpoint.
    if (element.version !== undefined && element.version !== null) {
      payload.version = element.version;
    }

    setSaveStatus("saving");

    const onAutosaveSuccess = (responseData) => {
      setSaveStatus("saved");
      // If this element is currently selected, merge server response (version, server-side changes).
      if (responseData && Object.keys(responseData).length > 0) {
        const pendingFields = autosaveRef.current?.getPendingFieldNames?.(endpoint) || [];
        setSelectedElement((prev) => {
          if (!prev) return prev;
          if (prev.id !== element.id || prev.type !== element.type) return prev;
          if (pendingFields.length === 0) {
            return { ...prev, ...responseData };
          }
          const safeMerge = { ...responseData };
          for (const pf of pendingFields) {
            delete safeMerge[pf];
          }
          return { ...prev, ...safeMerge };
        });
      }
      // Ensure the rendered canvas catches up (many views render from parent queries)
      try {
        refreshElement?.(element.id, element.type);
      } catch (_) {}
      dispatchNavbarRefreshForElement(element, "history");
    };

    const onAutosaveError = async (error) => {
      console.error("[EditContext] Autosave error (history patch):", error);

      if (error?.status === 412) {
        const elType = element?.type;
        const elId = element?.id;
        if (!elType || elId == null) {
          setSaveStatus("error");
          return;
        }

        const key = `${elType}:${elId}`;
        const now = Date.now();
        const prev = versionConflictRetryRef.current.get(key);
        const RETRY_WINDOW_MS = 3000;
        const MAX_RETRIES = 1;
        const withinWindow = prev && (now - prev.ts) < RETRY_WINDOW_MS;
        const count = withinWindow ? prev.count : 0;
        if (count >= MAX_RETRIES) {
          setSaveStatus("error");
          return;
        }
        versionConflictRetryRef.current.set(key, { ts: now, count: count + 1 });

        try {
          const getEndpoint = getFetchEndpointForVersionRecovery(elType, elId);
          if (!getEndpoint) {
            setSaveStatus("error");
            return;
          }

          const res = await apiFetch(getEndpoint, { credentials: "include" });
          if (!res.ok) {
            setSaveStatus("error");
            return;
          }

          const fresh = await res.json();
          const freshVersion = Number(fresh?.version);
          if (!Number.isFinite(freshVersion)) {
            setSaveStatus("error");
            return;
          }

          const retryPayload = { ...payload, version: freshVersion };
          autosaveRef.current?.schedule(endpoint, retryPayload, onAutosaveSuccess, onAutosaveError, "PATCH");
          return;
        } catch (e) {
          console.error("[EditContext] Version conflict recovery failed (history patch):", e);
          setSaveStatus("error");
          return;
        }
      }

      setSaveStatus("error");
    };

    autosaveRef.current.schedule(endpoint, payload, onAutosaveSuccess, onAutosaveError, "PATCH");
  }, [
    autosaveRef,
    getUpdateEndpoint,
    getFetchEndpointForVersionRecovery,
    normaliseBackgroundPayloadForElement,
    toApiPayloadValue,
    selectedElement,
    refreshElement,
    dispatchNavbarRefreshForElement,
  ]);

  // ---------------------------------------------------------------------------
  // Move undo/redo helpers
  // ---------------------------------------------------------------------------

  const getNavbarSectionNavbarId = useCallback((section) => {
    return section?.navbar?.id ?? section?.navbar ?? null;
  }, []);

  const getRelationId = useCallback((value) => {
    if (value && typeof value === "object") return value.id ?? null;
    return value ?? null;
  }, []);

  const buildNavbarSectionCreatePayload = useCallback(
    (section) => {
      const label = section?.label || {};
      return {
        order: Number.isFinite(Number(section?.order)) ? Number(section.order) : 0,
        label: {
          content: label.content || label.text || "New Section",
          type: label.type || "text",
        },
        link_choice: section?.link_choice || "internal",
        link_external: section?.link_external || null,
        link_internal: getRelationId(section?.link_internal),
      };
    },
    [getRelationId],
  );

  const stripNestedIds = useCallback((value) => {
    if (Array.isArray(value)) return value.map((item) => stripNestedIds(item));
    if (!value || typeof value !== "object") return value;
    const next = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      if (key === "id") continue;
      next[key] = stripNestedIds(nestedValue);
    }
    return next;
  }, []);

  const refreshAfterNavbarSectionHistory = useCallback(
    (entry, reason) => {
      if (entry?.websiteId) {
        try {
          window.dispatchEvent(
            new CustomEvent("editor:refresh-navbar", {
              detail: {
                websiteId: entry.websiteId,
                reason,
                elementType: "navbar_section",
                elementId: entry.restoredElement?.id ?? entry.element?.id ?? null,
                parentNavbarId: entry.navbarId ?? null,
              },
            }),
          );
        } catch (_) {}
      }
      if (entry?.websiteId) {
        queryClient.invalidateQueries({ queryKey: ["page-skeleton", entry.websiteId] });
        queryClient.invalidateQueries({ queryKey: ["website", entry.websiteId] });
      }
    },
    [queryClient],
  );

  const restoreDeletedNavbarSection = useCallback(
    async (entry) => {
      const navbarId = entry?.navbarId;
      const snapshot = entry?.element;
      if (!navbarId || !snapshot) return;

      const createResponse = await apiFetch(`/api/websites/${navbarId}/navbar/sections/create/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(buildNavbarSectionCreatePayload(snapshot)),
      });

      if (!createResponse.ok) {
        throw new Error(`Failed to restore navbar section (${createResponse.status})`);
      }

      let created = {};
      try {
        created = await createResponse.json();
      } catch (_) {
        created = {};
      }

      if (created?.id != null) {
        const restorePayload = {
          ...snapshot,
          id: created.id,
          navbar: navbarId,
          label: stripNestedIds(snapshot.label),
          base_style: stripNestedIds(snapshot.base_style),
          link_internal: getRelationId(snapshot.link_internal),
        };
        try {
          const patchResponse = await apiFetch(`/api/websites/navbar/sections/${created.id}/update/`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(restorePayload),
          });
          if (patchResponse.ok) {
            created = await patchResponse.json().catch(() => created);
          }
        } catch (_) {}
      }

      entry.restoredElement = {
        ...snapshot,
        ...created,
        type: "navbar_section",
        navbar: snapshot.navbar,
      };
      refreshAfterNavbarSectionHistory(entry, "history-undo-delete");
    },
    [buildNavbarSectionCreatePayload, getRelationId, refreshAfterNavbarSectionHistory, stripNestedIds],
  );

  const deleteRestoredNavbarSection = useCallback(
    async (entry) => {
      const navbarId = entry?.navbarId;
      const restoredId = entry?.restoredElement?.id;
      if (!navbarId || restoredId == null) return;

      const response = await apiFetch(`/api/websites/${navbarId}/navbar/sections/${restoredId}/delete/`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to redo navbar section delete (${response.status})`);
      }

      entry.restoredElement = null;
      refreshAfterNavbarSectionHistory(entry, "history-redo-delete");
    },
    [refreshAfterNavbarSectionHistory],
  );

  /** Apply a single move target (used by undo/redo for kind:"move" entries). */
  const applyMoveTarget = useCallback(async (entityType, entityId, itemType, target) => {
    if (entityType === "component") {
      if (target.parentType === "page") {
        await moveComponentApi({ id: entityId, toParentWebsitePageId: target.parentId, order: target.order });
      } else if (target.parentType === "component") {
        await moveComponentApi({ id: entityId, toParentComponentId: target.parentId, order: target.order });
      }
    } else {
      // item
      if (target.parentType === "component") {
        await moveItemApi({ type: itemType, id: entityId, toComponentId: target.parentId, order: target.order });
      } else if (target.parentType === "hero") {
        await moveItemApi({ type: itemType, id: entityId, toParentHeroId: target.parentId, order: target.order });
      }
    }
  }, []);

  /** Invalidate React Query caches for both sides of a move (source + destination). */
  const invalidateAfterMove = useCallback((moveEntry) => {
    const targets = [moveEntry.from, moveEntry.to].filter(Boolean);
    for (const t of targets) {
      if (t.parentType === "page") {
        if (moveEntry.websiteId) {
          queryClient.invalidateQueries({ queryKey: ["page-skeleton", moveEntry.websiteId] });
        }
      } else if (t.parentType === "component") {
        queryClient.invalidateQueries({ queryKey: ["component", t.parentId] });
      } else if (t.parentType === "hero") {
        queryClient.invalidateQueries({ queryKey: ["item", "hero", t.parentId] });
        if (t.parentComponentId) {
          queryClient.invalidateQueries({ queryKey: ["component", t.parentComponentId] });
        }
      }
    }
  }, [queryClient]);

  // ---------------------------------------------------------------------------
  // Undo / Redo
  // ---------------------------------------------------------------------------

  const undo = useCallback(async () => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    const entry = stack.pop();
    redoStackRef.current.push(entry);
    syncHistoryFlags();

    applyingHistoryRef.current = true;
    try {
      if (entry.kind === "move") {
        // Reverse the move: apply the "from" target (move back to original position)
        setSaveStatus("saving");
        try {
          await applyMoveTarget(entry.entityType, entry.entityId, entry.itemType, entry.from);
          invalidateAfterMove(entry);
          setSaveStatus("saved");
        } catch (err) {
          console.error("[EditContext] Undo move failed:", err);
          setSaveStatus("error");
        }
      } else if (entry.kind === "delete" && entry.elementType === "navbar_section") {
        setSaveStatus("saving");
        try {
          await restoreDeletedNavbarSection(entry);
          setSaveStatus("saved");
        } catch (err) {
          console.error("[EditContext] Undo delete failed:", err);
          setSaveStatus("error");
        }
      } else {
        // Field-level undo (existing logic)
        const inverse = {};
        for (const [field, change] of Object.entries(entry.updates || {})) {
          inverse[field] = cloneForHistory(change?.from);
        }
        schedulePatchForElement(entry.element, inverse, { optimistic: true });
      }
    } finally {
      applyingHistoryRef.current = false;
    }
  }, [applyMoveTarget, cloneForHistory, invalidateAfterMove, restoreDeletedNavbarSection, schedulePatchForElement, syncHistoryFlags]);

  const redo = useCallback(async () => {
    const stack = redoStackRef.current;
    if (!stack.length) return;
    const entry = stack.pop();
    undoStackRef.current.push(entry);
    syncHistoryFlags();

    applyingHistoryRef.current = true;
    try {
      if (entry.kind === "move") {
        // Re-apply the move: apply the "to" target
        setSaveStatus("saving");
        try {
          await applyMoveTarget(entry.entityType, entry.entityId, entry.itemType, entry.to);
          invalidateAfterMove(entry);
          setSaveStatus("saved");
        } catch (err) {
          console.error("[EditContext] Redo move failed:", err);
          setSaveStatus("error");
        }
      } else if (entry.kind === "delete" && entry.elementType === "navbar_section") {
        setSaveStatus("saving");
        try {
          await deleteRestoredNavbarSection(entry);
          setSaveStatus("saved");
        } catch (err) {
          console.error("[EditContext] Redo delete failed:", err);
          setSaveStatus("error");
        }
      } else {
        // Field-level redo (existing logic)
        const forward = {};
        for (const [field, change] of Object.entries(entry.updates || {})) {
          forward[field] = cloneForHistory(change?.to);
        }
        schedulePatchForElement(entry.element, forward, { optimistic: true });
      }
    } finally {
      applyingHistoryRef.current = false;
    }
  }, [applyMoveTarget, cloneForHistory, deleteRestoredNavbarSection, invalidateAfterMove, schedulePatchForElement, syncHistoryFlags]);

  // Global keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z (redo), Ctrl+Y (redo)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e) return;
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;
      if (e.altKey) return;
      if (shouldIgnoreUndoShortcutTarget(e.target)) return;

      const key = String(e.key || "").toLowerCase();
      const isZ = key === "z";
      const isY = key === "y";
      if (!isZ && !isY) return;

      // Don't interfere with browser/OS undo inside inputs; we filtered above.
      if (isZ && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((isZ && e.shiftKey) || isY) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [redo, shouldIgnoreUndoShortcutTarget, undo]);

  // Listen for move events dispatched by onDragEnd (which lives outside EditProvider)
  // and push them onto the undo stack so Ctrl+Z can reverse drag-and-drop moves.
  useEffect(() => {
    const handleRecordMove = (e) => {
      const detail = e?.detail;
      if (!detail || !detail.entityId) return;
      if (applyingHistoryRef.current) return; // don't record when undo/redo is replaying

      const entry = {
        ts: Date.now(),
        kind: "move",
        entityType: detail.entityType,   // "component" | "item"
        entityId: detail.entityId,
        itemType: detail.itemType || null,
        from: detail.from,               // { parentType, parentId, parentComponentId?, order }
        to: detail.to,                   // { parentType, parentId, parentComponentId?, order }
        websiteId: detail.websiteId ?? websiteId,
      };

      undoStackRef.current.push(entry);

      // New user action invalidates redo history
      if (redoStackRef.current.length) {
        redoStackRef.current = [];
      }

      // Enforce max size
      if (undoStackRef.current.length > MAX_HISTORY_ENTRIES) {
        undoStackRef.current.splice(0, undoStackRef.current.length - MAX_HISTORY_ENTRIES);
      }

      syncHistoryFlags();
    };

    window.addEventListener("editor:record-move", handleRecordMove);
    return () => window.removeEventListener("editor:record-move", handleRecordMove);
  }, [syncHistoryFlags, websiteId]);

  // Select an element
  const selectElement = useCallback((element) => {
    setSelectedElement(element);
    setIsAddMode(false);
    setAddModeTarget(null);
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedElement(null);
    setIsAddMode(false);
    setAddModeTarget(null);
  }, []);

  // Request that the EditPanel focuses a given field for a given element.
  // This does NOT mutate any backend data; it is purely a UI affordance.
  const requestEditPanelFocus = useCallback(
    (element, field, options = {}) => {
      if (!element || !field) return;
      const { selectAll = true } = options || {};

      // Ensure the element is selected so its metadata/fields are visible in the panel
      if (
        !selectedElement ||
        selectedElement.id !== element.id ||
        selectedElement.type !== element.type
      ) {
        selectElement(element);
      }

      editPanelFocusSeqRef.current += 1;
      setEditPanelFocusRequest({
        elementId: element.id,
        elementType: element.type,
        field,
        selectAll,
        requestId: editPanelFocusSeqRef.current,
      });
    },
    [selectedElement, selectElement],
  );

  const clearEditPanelFocusRequest = useCallback(() => {
    setEditPanelFocusRequest(null);
  }, []);

  // Trigger add mode - now accepts full target object with order
  const triggerAddMode = useCallback((target) => {
    console.log('🔍 ADDBUTTON_ORDER_BUG - triggerAddMode called:', {
      target,
      hasOrder: typeof target?.order === 'number',
      orderValue: target?.order,
    });
    setIsAddMode(true);
    setAddModeTarget(target);
    setSelectedElement(null);
  }, []);

  // Exit add mode
  const exitAddMode = useCallback(() => {
    setIsAddMode(false);
    setAddModeTarget(null);
  }, []);

  // Start inline editing for a specific element and field
  const startInlineEdit = useCallback((element, field) => {
    if (!element || !field) {
      console.warn('[EditContext] startInlineEdit called without element or field');
      return;
    }
    setInlineEditingElement({ id: element.id, type: element.type, field });
    // Also select the element if not already selected
    if (!selectedElement || selectedElement.id !== element.id || selectedElement.type !== element.type) {
      selectElement(element);
    }
  }, [selectedElement, selectElement]);

  // Stop inline editing
  const stopInlineEdit = useCallback(() => {
    setInlineEditingElement(null);
  }, []);

  // Check if a specific element and field is being edited inline
  const isInlineEditing = useCallback((element, field) => {
    if (!inlineEditingElement || !element) return false;
    return (
      inlineEditingElement.id === element.id &&
      inlineEditingElement.type === element.type &&
      inlineEditingElement.field === field
    );
  }, [inlineEditingElement]);

  // Update field with autosave
  const normaliseBackgroundPayload = useCallback((fieldName, incomingValue) => {
    if (!selectedElement) {
      return incomingValue;
    }

    return normaliseBackgroundPayloadForElement(selectedElement, fieldName, incomingValue);
  }, [selectedElement]);

  const updateField = useCallback((fieldName, rawValue, optimisticExtras = {}) => {
    if (!selectedElement) {
      console.warn('[EditContext] updateField called with no selected element');
      return;
    }

    // Record history BEFORE optimistic mutation.
    try {
      const prevValue = cloneForHistory(selectedElement?.[fieldName]);
      const nextValue = cloneForHistory(rawValue);
      recordHistoryEntry(selectedElement, {
        [fieldName]: { from: prevValue, to: nextValue },
      });
    } catch (_) {}

    const value = normaliseBackgroundPayload(fieldName, rawValue);
    // Some fields want a richer local shape for UX, but must be serialized as primitives for the API.
    // Example: link_internal is a FK to WebsitePage; we keep the page object locally but PATCH only its id.
    const payloadValue = (() => {
      if (fieldName === "link_internal") {
        if (value && typeof value === "object") {
          const maybeId = value.id;
          if (typeof maybeId === "number" && Number.isFinite(maybeId)) return maybeId;
          if (typeof maybeId === "string" && maybeId.trim()) {
            const parsed = parseInt(maybeId, 10);
            if (!Number.isNaN(parsed)) return parsed;
          }
        }
      }
      return value;
    })();
    if (fieldName === "background" || fieldName === "background_pattern" || fieldName === "pattern" || fieldName === "colors") {
      console.debug('[HERO_PATTERN_DEBUG] updateField payload', {
        fieldName,
        value,
      });
    }

    // Optimistically update local state
    // For Text items with content updates, also update rendered_content optimistically
    const optimisticUpdate = { [fieldName]: value };
    if (optimisticExtras._optimistic_rendered_content) {
      optimisticUpdate.rendered_content = optimisticExtras._optimistic_rendered_content;
    }
    setSelectedElement(prev => ({ ...prev, ...optimisticUpdate }));
    
    // Get endpoint
    const endpoint = getUpdateEndpoint(selectedElement);
    if (!endpoint) {
      console.error('[EditContext] No update endpoint for element:', selectedElement);
      setSaveStatus('error');
      return;
    }
    
    // Schedule autosave
    setSaveStatus('saving');
    
    if (!autosaveRef.current) {
      console.error('[EditContext] Autosave not initialized');
      setSaveStatus('error');
      return;
    }
    
    // Build payload with version for optimistic locking
    const payload = { [fieldName]: payloadValue };
    
    // Include version if element has it (for optimistic locking)
    if (selectedElement.version !== undefined && selectedElement.version !== null) {
      payload.version = selectedElement.version;
    }
    
    const onAutosaveSuccess = (responseData) => {
      // Success
      setSaveStatus('saved');

      const { type, id } = selectedElement;
      try {
        versionConflictRetryRef.current.delete(`${type}:${id}`);
      } catch (_) {}

      // Update with server response to get new version and any server-side changes.
      // website_page responses wrap data under a "page" key: { message, page: {...} }
      const mergeData =
        type === "website_page" && responseData?.page && typeof responseData.page === "object"
          ? responseData.page
          : responseData;
      if (mergeData && Object.keys(mergeData).length > 0) {
        const updatedId = selectedElement?.id;
        const updatedType = selectedElement?.type;

        // Detect fields that are still queued in the autosave buffer (optimistic
        // changes the user made AFTER this PATCH was sent). Merging the full
        // server response would overwrite those pending optimistic values,
        // causing the UI to "revert" until the next autosave round-trips.
        const pendingFields = autosaveRef.current?.getPendingFieldNames?.(endpoint) || [];

        setSelectedElement(prev => {
          if (!prev) return prev;
          if (prev.id !== updatedId || prev.type !== updatedType) return prev;
          if (pendingFields.length === 0) {
            return { ...prev, ...mergeData };
          }
          const safeMerge = { ...mergeData };
          for (const pf of pendingFields) {
            delete safeMerge[pf];
          }
          return { ...prev, ...safeMerge };
        });
      }

      // Invalidate React Query cache to trigger refetch with updated data
      const itemTypes = ['hero', 'title', 'text', 'image', 'video', 'button', 'button_collection', 'file', 'image_collection'];

      if (itemTypes.includes(type)) {
        // Invalidate item query
        queryClient.invalidateQueries({ queryKey: ["item", type, id] });
        console.log(`[EditContext] Invalidated query: ["item", "${type}", ${id}]`);

        // If this is a hero, also invalidate its parent component
        if (type === 'hero' && selectedElement.component) {
          queryClient.invalidateQueries({ queryKey: ["component", selectedElement.component] });
          console.log(`[EditContext] Invalidated parent component query: ["component", ${selectedElement.component}]`);
        }

        // Hero updates can mutate slotted children as a server-side side
        // effect (e.g. ``apply_to_hero`` re-pinning ``text_position`` and
        // zeroing margin/padding when the user switches modes). The
        // children render through their own ``["item", type, id]`` queries,
        // so without invalidating them the editor would keep displaying
        // the *pre-mutation* character_style until the user happens to
        // click the child item. The PATCH response carries the children
        // as minimal refs — exactly what we need for the invalidation.
        if (type === "hero") {
          invalidateHeroChildrenQueries(mergeData);
        }

        // If this item is inside a hero, also invalidate the parent hero query
        // This ensures Hero re-renders with updated child data (critical for ButtonCollection buttons_per_row)
        if (selectedElement.hero) {
          queryClient.invalidateQueries({ queryKey: ["item", "hero", selectedElement.hero] });
          console.log(`[EditContext] Invalidated parent Hero query: ["item", "hero", ${selectedElement.hero}]`);
        }
      } else if (type === 'component') {
        // Invalidate component query
        queryClient.invalidateQueries({ queryKey: ["component", id] });
        console.log(`[EditContext] Invalidated query: ["component", ${id}]`);
      } else if (type === 'website_page') {
        // Page changes should refresh the page skeleton (which drives WebsitePage.js rendering)
        if (websiteId) {
          queryClient.invalidateQueries({ queryKey: ["page-skeleton", websiteId] });
          console.log(`[EditContext] Invalidated query: ["page-skeleton", ${websiteId}]`);
        }
      } else if (type === 'navbar') {
        // Navbar is fetched differently, may need special handling
        // For now, force a full page refresh after navbar updates
        console.log(`[EditContext] Navbar updated - consider page refresh`);
      }

      // Navigation elements are rendered from renderer state (Website.js / SideBar.js),
      // but we should NOT dispatch the broad `editor:refresh-website` on autosaves because
      // EditWebsitePage listens to it and refetches the whole editor (feels like a full reload).
      // Instead, emit targeted refresh events.
      if (websiteId) {
        try {
          const shouldRefreshNavbar =
            type === "navbar" ||
            type === "navbar_section" ||
            (type === "title" && selectedElement?.__editorParentType === "navbar");

          if (shouldRefreshNavbar) {
            window.dispatchEvent(
              new CustomEvent("editor:refresh-navbar", {
                detail: { websiteId, reason: "autosave", elementType: type, elementId: id },
              }),
            );
          } else if (type === "footer") {
            window.dispatchEvent(
              new CustomEvent("editor:refresh-footer", {
                detail: { websiteId, reason: "autosave", elementType: type, elementId: id },
              }),
            );
          } else if (type === "sidebar" || type === "sidebar_section") {
            const side = selectedElement?.side || selectedElement?.sidebar?.side || null;
            window.dispatchEvent(
              new CustomEvent("editor:refresh-sidebar", {
                detail: { websiteId, reason: "autosave", elementType: type, elementId: id, side },
              }),
            );
          }
        } catch (_) {
          // no-op (e.g., tests/SSR)
        }
      }
    };

    const onAutosaveError = async (error) => {
      // Error
      console.error('[EditContext] Autosave error:', error);

      if (error?.status === 412) {
        console.warn('[EditContext] Version conflict detected - attempting auto-recovery');

        const elType = selectedElement?.type;
        const elId = selectedElement?.id;
        if (!elType || elId == null) {
          setSaveStatus('error');
          return;
        }

        const key = `${elType}:${elId}`;
        const now = Date.now();
        const prev = versionConflictRetryRef.current.get(key);
        const RETRY_WINDOW_MS = 3000;
        const MAX_RETRIES = 1;
        const withinWindow = prev && (now - prev.ts) < RETRY_WINDOW_MS;
        const count = withinWindow ? prev.count : 0;

        if (count >= MAX_RETRIES) {
          setSaveStatus('error');
          return;
        }

        versionConflictRetryRef.current.set(key, { ts: now, count: count + 1 });

        try {
          const itemTypes = ['hero', 'title', 'text', 'image', 'video', 'button', 'button_collection', 'file', 'image_collection'];
          const getEndpoint = itemTypes.includes(elType)
            ? `/api/items/${elType}/${elId}/`
            : elType === "component"
              ? `/api/components/${elId}/`
              : null;

          if (!getEndpoint) {
            setSaveStatus('error');
            return;
          }

          setSaveStatus('saving');

          const res = await apiFetch(getEndpoint, { credentials: 'include' });
          if (!res.ok) {
            setSaveStatus('error');
            return;
          }

          const fresh = await res.json();
          const freshVersion = Number(fresh?.version);
          if (!Number.isFinite(freshVersion)) {
            setSaveStatus('error');
            return;
          }

          // Update the selected element to the latest version but keep local optimistic edits.
          setSelectedElement(prevSel => {
            if (!prevSel) return prevSel;
            if (prevSel.id !== elId || prevSel.type !== elType) return prevSel;
            return { ...fresh, ...prevSel, version: freshVersion };
          });

          // Retry with refreshed version (last-write-wins).
          const retryPayload = { ...payload, version: freshVersion };
          autosaveRef.current?.schedule(endpoint, retryPayload, onAutosaveSuccess, onAutosaveError, 'PATCH');
          return;
        } catch (e) {
          console.error('[EditContext] Version conflict recovery failed:', e);
          setSaveStatus('error');
          return;
        }
      }

      setSaveStatus('error');
    };

    autosaveRef.current.schedule(endpoint, payload, onAutosaveSuccess, onAutosaveError, 'PATCH');
  }, [selectedElement, getUpdateEndpoint, queryClient, normaliseBackgroundPayload, websiteId, invalidateHeroChildrenQueries]);

  // Update multiple fields in a single autosave payload (prevents debounce overwrites)
  const updateMultipleFields = useCallback((updates) => {
    if (!selectedElement) {
      console.warn('[EditContext] updateMultipleFields called with no selected element');
      return;
    }

    // Record history BEFORE optimistic mutation.
    try {
      const historyUpdates = {};
      for (const [k, v] of Object.entries(updates || {})) {
        historyUpdates[k] = { from: cloneForHistory(selectedElement?.[k]), to: cloneForHistory(v) };
      }
      if (Object.keys(historyUpdates).length) {
        recordHistoryEntry(selectedElement, historyUpdates);
      }
    } catch (_) {}

    // Optimistically update local state
    setSelectedElement(prev => ({ ...prev, ...updates }));

    const endpoint = getUpdateEndpoint(selectedElement);
    if (!endpoint) {
      console.error('[EditContext] No update endpoint for element:', selectedElement);
      setSaveStatus('error');
      return;
    }

    setSaveStatus('saving');

    if (!autosaveRef.current) {
      console.error('[EditContext] Autosave not initialized');
      setSaveStatus('error');
      return;
    }

    const payload = { ...updates };
    if (selectedElement.version !== undefined && selectedElement.version !== null) {
      payload.version = selectedElement.version;
    }

    const onAutosaveSuccess = (responseData) => {
      setSaveStatus('saved');

      const { type, id } = selectedElement;
      try {
        versionConflictRetryRef.current.delete(`${type}:${id}`);
      } catch (_) {}

      if (responseData && Object.keys(responseData).length > 0) {
        const updatedId = selectedElement?.id;
        const updatedType = selectedElement?.type;
        const pendingFields = autosaveRef.current?.getPendingFieldNames?.(endpoint) || [];

        setSelectedElement(prev => {
          if (!prev) return prev;
          if (prev.id !== updatedId || prev.type !== updatedType) return prev;
          if (pendingFields.length === 0) {
            return { ...prev, ...responseData };
          }
          const safeMerge = { ...responseData };
          for (const pf of pendingFields) {
            delete safeMerge[pf];
          }
          return { ...prev, ...safeMerge };
        });
      }
      const itemTypes = ['hero', 'title', 'text', 'image', 'video', 'button', 'button_collection', 'file', 'image_collection'];
      if (itemTypes.includes(type)) {
        queryClient.invalidateQueries({ queryKey: ["item", type, id] });
        if (type === 'hero' && selectedElement.component) {
          queryClient.invalidateQueries({ queryKey: ["component", selectedElement.component] });
        }
        // Mirror the single-field setter: hero updates can mutate child
        // items via ``apply_to_hero`` (mode switch / slot promote). The
        // children render through their own ``["item", type, id]`` query;
        // refresh those so the editor surfaces the new character_style
        // immediately.
        if (type === "hero") {
          invalidateHeroChildrenQueries(responseData);
        }
        if (selectedElement.hero) {
          queryClient.invalidateQueries({ queryKey: ["item", "hero", selectedElement.hero] });
        }
      } else if (type === 'component') {
        queryClient.invalidateQueries({ queryKey: ["component", id] });
      } else if (type === 'website_page') {
        if (websiteId) {
          queryClient.invalidateQueries({ queryKey: ["page-skeleton", websiteId] });
        }
      }

      if (websiteId) {
        try {
          const shouldRefreshNavbar =
            type === "navbar" ||
            type === "navbar_section" ||
            (type === "title" && selectedElement?.__editorParentType === "navbar");

          if (shouldRefreshNavbar) {
            window.dispatchEvent(
              new CustomEvent("editor:refresh-navbar", {
                detail: { websiteId, reason: "autosave", elementType: type, elementId: id },
              }),
            );
          } else if (type === "footer") {
            window.dispatchEvent(
              new CustomEvent("editor:refresh-footer", {
                detail: { websiteId, reason: "autosave", elementType: type, elementId: id },
              }),
            );
          } else if (type === "sidebar" || type === "sidebar_section") {
            const side = selectedElement?.side || selectedElement?.sidebar?.side || null;
            window.dispatchEvent(
              new CustomEvent("editor:refresh-sidebar", {
                detail: { websiteId, reason: "autosave", elementType: type, elementId: id, side },
              }),
            );
          }
        } catch (_) {}
      }
    };

    const onAutosaveError = async (error) => {
      console.error('[EditContext] Autosave error (multi):', error);

      if (error?.status === 412) {
        console.warn('[EditContext] Version conflict detected in updateMultipleFields - attempting auto-recovery');

        const elType = selectedElement?.type;
        const elId = selectedElement?.id;
        if (!elType || elId == null) {
          setSaveStatus('error');
          return;
        }

        const key = `${elType}:${elId}`;
        const now = Date.now();
        const prev = versionConflictRetryRef.current.get(key);
        const RETRY_WINDOW_MS = 3000;
        const MAX_RETRIES = 1;
        const withinWindow = prev && (now - prev.ts) < RETRY_WINDOW_MS;
        const count = withinWindow ? prev.count : 0;

        if (count >= MAX_RETRIES) {
          setSaveStatus('error');
          return;
        }
        versionConflictRetryRef.current.set(key, { ts: now, count: count + 1 });

        try {
          const itemTypes = ['hero', 'title', 'text', 'image', 'video', 'button', 'button_collection', 'file', 'image_collection'];
          const getEndpoint = itemTypes.includes(elType)
            ? `/api/items/${elType}/${elId}/`
            : elType === "component"
              ? `/api/components/${elId}/`
              : null;

          if (!getEndpoint) {
            setSaveStatus('error');
            return;
          }

          setSaveStatus('saving');
          const res = await apiFetch(getEndpoint, { credentials: 'include' });
          if (!res.ok) {
            setSaveStatus('error');
            return;
          }

          const fresh = await res.json();
          const freshVersion = Number(fresh?.version);
          if (!Number.isFinite(freshVersion)) {
            setSaveStatus('error');
            return;
          }

          setSelectedElement(prevSel => {
            if (!prevSel) return prevSel;
            if (prevSel.id !== elId || prevSel.type !== elType) return prevSel;
            return { ...fresh, ...prevSel, version: freshVersion };
          });

          const retryPayload = { ...payload, version: freshVersion };
          autosaveRef.current?.schedule(endpoint, retryPayload, onAutosaveSuccess, onAutosaveError, 'PATCH');
          return;
        } catch (e) {
          console.error('[EditContext] Version conflict recovery failed (multi):', e);
          setSaveStatus('error');
          return;
        }
      }

      setSaveStatus('error');
    };

    autosaveRef.current.schedule(endpoint, payload, onAutosaveSuccess, onAutosaveError, 'PATCH');
  }, [selectedElement, getUpdateEndpoint, queryClient, websiteId, invalidateHeroChildrenQueries]);

  const applyStylePreset = useCallback(async (presetName) => {
    if (!selectedElement?.type || selectedElement?.id == null) {
      throw new Error("No editable element is currently selected.");
    }

    const elementId = selectedElement.id;
    const elementType = selectedElement.type;

    setSaveStatus("saving");

    let response;
    try {
      response = await apiFetch(
        `/api/style-presets/elements/${elementType}/${elementId}/apply/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ preset_name: presetName }),
          credentials: "include",
        },
      );
    } catch (error) {
      setSaveStatus("error");
      throw error;
    }

    if (!response.ok) {
      let message = `Failed to apply preset (${response.status})`;
      try {
        const data = await response.json();
        message = data?.error || data?.detail || message;
      } catch (_) {
        try {
          const text = await response.text();
          if (text) message = text;
        } catch (_) {}
      }
      setSaveStatus("error");
      throw new Error(message);
    }

    let responseData = {};
    try {
      responseData = await response.json();
    } catch (_) {
      responseData = {};
    }

    setSelectedElement((prev) => {
      if (!prev) return prev;
      if (prev.id !== elementId || prev.type !== elementType) return prev;
      return { ...prev, ...responseData };
    });

    setSaveStatus("saved");

    try {
      queryClient.invalidateQueries();
    } catch (_) {}

    try {
      await refreshElement(elementId, elementType);
    } catch (_) {
      // Best effort; the optimistic merge above keeps the panel responsive.
    }

    return responseData;
  }, [queryClient, refreshElement, selectedElement]);

  // Delete element
  const deleteElement = useCallback(async (element) => {
    if (!element) {
      console.warn('[EditContext] deleteElement called with no element');
      return;
    }

    const { type, id } = element;
    
    // Determine delete endpoint
    let endpoint = null;
    const itemTypes = ['title', 'text', 'image', 'video', 'button', 'button_collection', 'hero', 'file'];
    
    if (itemTypes.includes(type)) {
      // Typed delete avoids ambiguous IDs across item tables
      endpoint = `/api/items/${type}/${id}/delete/`;
    } else if (type === 'component') {
      endpoint = `/api/components/${id}/delete/`;
    } else if (type === 'image_collection') {
      endpoint = `/api/imagecollections/${id}/delete/`;
    } else if (type === 'navbar_section') {
      const navbarId = element.navbar?.id || element.navbar;
      if (navbarId) {
        endpoint = `/api/websites/${navbarId}/navbar/sections/${id}/delete/`;
      }
    } else if (type === 'sidebar_section') {
      const sidebarId = element.sidebar?.id || element.sidebar;
      if (sidebarId) {
        const isLeft = element.sidebar?.side === 'left' || element.side === 'left';
        const sidebarType = isLeft ? 'leftsidebar' : 'rightsidebar';
        endpoint = `/api/websites/${sidebarId}/${sidebarType}/sections/${id}/delete/`;
      }
    }
    
    if (!endpoint) {
      console.error('[EditContext] No delete endpoint for element:', element);
      return;
    }

    // Before deleting, check if this item is the sole child of its parent component.
    // If so, we'll cascade-delete the now-empty parent component after the item is removed.
    let shouldDeleteParentComponent = false;
    const parentComponentId = element.component;
    const isDirectComponentChild = parentComponentId && type !== 'component';
    if (isDirectComponentChild) {
      const cachedComponent = queryClient.getQueryData(["component", parentComponentId]);
      const children = cachedComponent?.all_child_components_and_items;
      if (Array.isArray(children) && children.length === 1) {
        const onlyChild = children[0];
        if (onlyChild && String(onlyChild.id) === String(id) && onlyChild.type === type) {
          shouldDeleteParentComponent = true;
        }
      }
    }
    
    try {
      const response = await apiFetch(endpoint, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[EditContext] DELETE failed with error:', errorText);
        
        // Parse error for better message
        let errorMessage = 'Failed to delete element';
        try {
          const errorObj = JSON.parse(errorText);
          if (errorObj.error === 'Not authorized') {
            errorMessage = 'You do not have permission to delete this item. Make sure you are logged in as the website owner.';
          } else {
            errorMessage = errorObj.error || errorMessage;
          }
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }
      
      const responseData = await response.json();

      if (type === "navbar_section") {
        const navbarId = getNavbarSectionNavbarId(element);
        pushHistoryEntry({
          ts: Date.now(),
          kind: "delete",
          elementType: "navbar_section",
          element: cloneForHistory(element),
          navbarId,
          websiteId: element.website ?? websiteId ?? null,
          responseData,
          restoredElement: null,
        });
      }

      // Remove any stale per-item cache immediately (prevents re-select showing ghost data)
      if (itemTypes.includes(type)) {
        queryClient.removeQueries({ queryKey: ["item", type, id] });
      }

      // Cascade: delete the parent component if the item we just removed was its only child.
      if (shouldDeleteParentComponent) {
        try {
          const compResponse = await apiFetch(`/api/components/${parentComponentId}/delete/`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (compResponse.ok) {
            queryClient.removeQueries({ queryKey: ["component", parentComponentId] });
          } else {
            console.warn('[EditContext] Cascade component delete failed, refreshing component instead');
            queryClient.invalidateQueries({ queryKey: ["component", parentComponentId] });
          }
        } catch (compErr) {
          console.warn('[EditContext] Cascade component delete error:', compErr);
          queryClient.invalidateQueries({ queryKey: ["component", parentComponentId] });
        }
      }
      
      // Refresh the minimum set of queries that drive what's rendered on-canvas.
      // Many screens render from Component + PageSkeleton data, so we update both when possible.
      if (element.component && !shouldDeleteParentComponent) {
        queryClient.invalidateQueries({ queryKey: ["component", element.component] });
      }
      if (element.hero) {
        queryClient.invalidateQueries({ queryKey: ["item", "hero", element.hero] });
      }
      if (websiteId) {
        queryClient.invalidateQueries({ queryKey: ["page-skeleton", websiteId] });
        queryClient.invalidateQueries({ queryKey: ["website", websiteId] });
        // Also refresh the editor's non-react-query website state (navbar pages list, etc.)
        window.dispatchEvent(
          new CustomEvent("editor:refresh-website", {
            detail: { websiteId, reason: "delete", elementType: type, elementId: id },
          }),
        );
        if (type === "navbar_section") {
          window.dispatchEvent(
            new CustomEvent("editor:refresh-navbar", {
              detail: {
                websiteId,
                reason: "delete",
                elementType: type,
                elementId: id,
                parentNavbarId: getNavbarSectionNavbarId(element),
              },
            }),
          );
        }
      }

      // Best-effort fallback if we couldn't infer any parent relationships.
      if (!element.component && !element.hero && !websiteId) {
        queryClient.invalidateQueries();
      }
      
      // Clear selection
      clearSelection();
    } catch (error) {
      console.error('[EditContext] Delete error:', error);
      alert(`Failed to delete: ${error.message}`);
    }
  }, [clearSelection, cloneForHistory, getNavbarSectionNavbarId, pushHistoryEntry, queryClient, websiteId]);

  // Update save status
  const updateSaveStatus = useCallback((status) => {
    setSaveStatus(status);
  }, []);

  // Compute locked fields for hero mode field suppression.
  // When the selected element is a child of a hero in automatic mode,
  // certain fields (margin, padding, order, and slot-specific fields like
  // font_size / letter_spacing) should be suppressed in the edit panel.
  const lockedFields = useMemo(() => {
    if (!selectedElement?.hero) return null;
    const heroData = queryClient.getQueryData(["item", "hero", selectedElement.hero]);
    if (!heroData || heroData.manual_layout !== false || !heroData.hero_mode) return null;

    const modeDef = heroData.active_mode_definition;
    if (!modeDef) return null;

    const locked = new Set(modeDef.locked_item_fields || []);

    const typeLocked = modeDef.type_locked_fields?.[selectedElement.type];
    if (Array.isArray(typeLocked)) {
      typeLocked.forEach((f) => locked.add(f));
    }

    // Add slot-specific locked fields if this item is assigned to a slot
    const slotAssignments = heroData.slot_assignments || {};
    for (const [slotName, slotItem] of Object.entries(slotAssignments)) {
      if (slotItem && slotItem.id === selectedElement.id) {
        const slotLocked = modeDef.slot_locked_fields?.[slotName];
        if (Array.isArray(slotLocked)) {
          slotLocked.forEach((f) => locked.add(f));
        }
        break;
      }
    }

    return locked.size > 0 ? locked : null;
  }, [selectedElement, queryClient]);

  const value = useMemo(() => ({
    websiteId,
    pageId,
    pageSubdomain,
    selectedElement,
    editMetadata,
    selectElement,
    clearSelection,
    updateField,
    updateMultipleFields,
    applyStylePreset,
    deleteElement,
    refreshElement,
    isAddMode,
    addModeTarget,
    triggerAddMode,
    exitAddMode,
    saveStatus,
    updateSaveStatus,
    inlineEditingElement,
    startInlineEdit,
    stopInlineEdit,
    isInlineEditing,
    editPanelFocusRequest,
    requestEditPanelFocus,
    clearEditPanelFocusRequest,
    updateElement: updateMultipleFields,
    lockedFields,

    // Undo/redo
    undo,
    redo,
    canUndo: historyFlags.canUndo,
    canRedo: historyFlags.canRedo,

    // Format painter
    formatPainter,
    armFormatPainter,
    clearFormatPainter,
    applyFormatPaint,
    isFormatPainterEligible,
  }), [
    websiteId,
    pageId,
    pageSubdomain,
    selectedElement,
    editMetadata,
    selectElement,
    clearSelection,
    updateField,
    updateMultipleFields,
    applyStylePreset,
    deleteElement,
    refreshElement,
    isAddMode,
    addModeTarget,
    triggerAddMode,
    exitAddMode,
    saveStatus,
    updateSaveStatus,
    inlineEditingElement,
    startInlineEdit,
    stopInlineEdit,
    isInlineEditing,
    editPanelFocusRequest,
    requestEditPanelFocus,
    clearEditPanelFocusRequest,
    lockedFields,

    undo,
    redo,
    historyFlags.canUndo,
    historyFlags.canRedo,

    formatPainter,
    armFormatPainter,
    clearFormatPainter,
    applyFormatPaint,
    isFormatPainterEligible,
  ]);

  return <EditContext.Provider value={value}>{children}</EditContext.Provider>;
};
